import { base44 } from '@/api/base44Client';
import { isMobileDevice } from './platform';

/**
 * Contact ownership and per-channel send rules, task: rework how the
 * welcome text and email are sent (2026-09-23). Shared by every screen that
 * can send a text or email to a contact (today: NewMeeting.jsx's
 * creation-time Save & Send, and EditContact.jsx's Resend Email / Resend
 * SMS), so the rule lives in exactly one place rather than being
 * reimplemented per screen.
 *
 * Email always sends automatically, server side, regardless of any of this
 * (unchanged, not represented here as a function: there is nothing to
 * decide). Only SMS's channel (native handoff vs Twilio) and whether either
 * channel needs a cross-owner confirmation first are decided here.
 */

/** contacts.scanned_by_user_id, however the caller's shape names it. */
function contactOwnerId(contact) {
  return contact?.scanned_by_user_id ?? contact?.scanned_by ?? null;
}

/**
 * Whether `user` is this contact's own owner. A contact with no owner at
 * all cannot happen in practice (every create requires a scanned_by), but
 * is treated as "owned by whoever is asking" rather than as a cross-owner
 * case, matching how worker/lib/outboundSend.ts's own load() falls back to
 * the caller in that situation.
 */
export function isContactOwner(user, contact) {
  const ownerId = contactOwnerId(contact);
  return !ownerId || user?.id === ownerId;
}

/**
 * Whether a text to this contact should hand off to the phone's own
 * Messages app instead of sending via Twilio. Only when all three are
 * true: the caller owns the contact, they are on their own mobile phone,
 * and this is not the shared booth device account (a device account never
 * owns a contact itself, real reps do, and it always sends via Twilio
 * instead, regardless of device).
 */
export function shouldUseNativeSmsHandoff(user, contact) {
  return !user?.is_device_account && isContactOwner(user, contact) && isMobileDevice();
}

/**
 * Whether sending to this contact needs the cross-owner confirmation
 * first. The shared device is exempt: its own rep picker already asks
 * "who is this for" before anything is saved (NewMeeting.jsx's
 * withRepConfirmation), and a device account can never literally match a
 * contact's scanned_by_user_id anyway (worker/routes/reps.ts excludes
 * device accounts from the pickable rep list), so this check would always
 * misfire "cross owner" for a device account's every single send if it
 * were not exempted.
 */
export function needsCrossOwnerConfirm(user, contact) {
  return !user?.is_device_account && !isContactOwner(user, contact);
}

/**
 * The cross-owner confirmation's exact copy, naming only the channel(s)
 * actually about to be sent so a single-channel resend (EditContact.jsx's
 * separate Email/SMS buttons) never claims to be sending more than it is.
 */
export function crossOwnerSendMessage(ownerName, { email, sms }) {
  const name = ownerName || "this contact's rep";
  if (email && sms) {
    return `You're trying to send a text message and email to a contact that belongs to "${name}." This text message and email will say that you are sending it from "${name}." The text message will be sent from Twilio and not from your personal cell phone. Do you want to proceed?`;
  }
  if (sms) {
    return `You're trying to send a text message to a contact that belongs to "${name}." This text message will say that you are sending it from "${name}." The text message will be sent from Twilio and not from your personal cell phone. Do you want to proceed?`;
  }
  return `You're trying to send an email to a contact that belongs to "${name}." This email will say that you are sending it from "${name}." Do you want to proceed?`;
}

/**
 * Sends the templated text to one contact, choosing native handoff or a
 * real Twilio send per shouldUseNativeSmsHandoff above. Resolves or throws
 * exactly like a plain base44.outbound.sendSms call either way, so a
 * caller's existing try/catch-per-channel handling needs no change: only
 * which path the send actually takes differs.
 */
export async function sendSmsToContact(user, contact) {
  if (!shouldUseNativeSmsHandoff(user, contact)) {
    await base44.outbound.sendSms(contact.id);
    return { handoff: false };
  }

  // Native handoff, task: send the Initial SMS from the rep's own phone
  // (2026-09-08, moved here 2026-09-23 so EditContact.jsx and
  // NewMeeting.jsx share one copy). The rendered body comes from the exact
  // same render() path the Twilio send uses (POST /api/outbound/sms/preview),
  // one source of truth for the wording. One encodeURIComponent() pass over
  // the whole body, `?&body=` to satisfy both iOS's and Android's de facto
  // parsers.
  const preview = await base44.outbound.previewSms(contact.id);
  if (!preview.to) {
    throw new Error(preview.message || 'That contact has no phone number this can text.');
  }
  window.location.href = `sms:${preview.to}?&body=${encodeURIComponent(preview.body)}`;
  // Optimistic, the owner's own call: a rep who abandons the message in
  // their own Messages app leaves a wrong timestamp here. No confirmation
  // step on purpose, nobody would tap it at a booth.
  await base44.outbound.smsNativeHandoff(contact.id, preview.body);
  return { handoff: true };
}
